import { MutableRefObject, useEffect, useReducer, useRef } from "react";
import {
  getCountFromServer,
  queryEqual,
  snapshotEqual,
  endBefore,
  limit,
  limitToLast,
  onSnapshot,
  query,
  startAfter,
  getDocs,
} from "firebase/firestore";

const defaultGuard = (state, a) => state;

const getReducer = () => (state, action) => {
  switch (action.type) {
    case "SET-QUERY": {
      const {
        query: queryObj,
        queryRef,
        firstDocRef,
        limit: limitNum,
        totalItems,
        totalPages,
        currentPage,
      } = action.payload;
      return {
        ...state,
        query: queryObj,
        queryRef,
        firstDocRef,
        limit: limitNum,
        isLoading: true,
        totalItems,
        totalPages,
        currentPage,
        pageCache: {}, // Clear cache when query changes
      };
    }

    case "LOAD": {
      const { value } = action.payload;
      const docs = value.docs;

      const items = docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));

      const firstDoc = docs[0];
      const lastDoc = docs[docs.length - 1];
      const queryFromRef = state.queryRef ? state.queryRef.current : undefined;

      // Cache the lastDoc of the current page for backward navigation
      // Page N+1 starts after Page N's lastDoc
      const newPageCache = { ...state.pageCache };
      if (lastDoc && state.currentPage > 0) {
        newPageCache[state.currentPage] = lastDoc;
      }

      // console.log("🔍 LOAD action - Debug info:", {
      //   currentPage: state.currentPage,
      //   docsLength: docs.length,
      //   hasQueryFromRef: !!queryFromRef,
      //   hasFirstDoc: !!firstDoc,
      //   hasLastDoc: !!lastDoc,
      //   hasStateLastQuery: !!state.lastQuery,
      //   firstDocId: firstDoc ? firstDoc.id : null,
      //   lastDocId: lastDoc ? lastDoc.id : null,
      //   firstDocRefId: state.firstDocRef && state.firstDocRef.current ? state.firstDocRef.current.id : null,
      //   cachedPages: Object.keys(newPageCache).join(','),
      // });

      // Fix: Use cached lastDoc from (currentPage-2) instead of unreliable endBefore+limitToLast
      // To go to page N, use startAfter(lastDoc of page N-1)
      let prevQuery;
      if (state.currentPage === 1) {
        prevQuery = undefined; // No previous page before page 1
      } else if (state.currentPage === 2) {
        // Going to page 1: use base query
        prevQuery = queryFromRef ? query(queryFromRef, limit(state.limit)) : undefined;
      } else {
        // Going to page N: use startAfter(lastDoc of page N-1)
        // We're on page M, going to page M-1, so we need lastDoc of page M-2
        const targetPage = state.currentPage - 1;
        const prevPageLastDoc = newPageCache[targetPage - 1];
        if (queryFromRef && prevPageLastDoc) {
          // console.log(`📍 Using cached lastDoc from page ${targetPage - 1} to navigate to page ${targetPage}`);
          prevQuery = query(queryFromRef, startAfter(prevPageLastDoc), limit(state.limit));
        } else {
          // console.log(`⚠️ No cached doc found for page ${targetPage - 1}, using fallback`);
          prevQuery = state.prevQuery; // Fallback to previous query
        }
      }

      const nextQuery =
        queryFromRef && lastDoc
          ? query(queryFromRef, startAfter(lastDoc), limit(state.limit))
          : state.nextQuery;

      // console.log("📝 Query construction result:", {
      //   usingFallbackForPrev: !(queryFromRef && firstDoc),
      //   hasPrevQuery: !!prevQuery,
      //   hasNextQuery: !!nextQuery,
      // });

      const firstDocRef = state.firstDocRef;
      if (firstDocRef && firstDocRef.current === undefined) {
        firstDocRef.current = firstDoc;
        // console.log("📌 Set firstDocRef.current (very first document)");
      }

      // Fix: Use currentPage only to avoid false positives from Firestore cursor ambiguity
      // When using multiple orderBy clauses, document comparison can be unreliable
      const isStartValue = state.currentPage <= 1;

      // console.log("🚦 isStart calculation:", {
      //   currentPage: state.currentPage,
      //   hasFirstDoc: !!firstDoc,
      //   hasFirstDocRef: !!(firstDocRef && firstDocRef.current),
      //   isEqual: firstDoc && firstDocRef && firstDocRef.current ? snapshotEqual(firstDoc, firstDocRef.current) : false,
      //   resultIsStart: isStartValue,
      // });

      // Fix: Use currentPage for isEnd as well for consistency
      const isEndValue = state.currentPage === state.totalPages || items.length < state.limit;

      return {
        ...state,
        docs,
        lastQuery: items.length > 0 ? state.query : undefined,
        isLoading: false,
        firstDoc,
        firstDocRef,
        lastDoc,
        prevQuery,
        nextQuery,
        items,
        isStart: isStartValue,
        isEnd: isEndValue,
        pageCache: newPageCache, // Update the page cache
      };
    }

    case "NEXT": {
      // console.log("➡️ NEXT action:", {
      //   from: state.currentPage,
      //   to: state.currentPage + 1,
      //   hasNextQuery: !!state.nextQuery,
      // });
      return {
        ...state,
        isLoading: true,
        currentPage: state.currentPage + 1,
        query: state.nextQuery,
      };
    }

    case "PREV": {
      // console.log("⬅️ PREV action:", {
      //   from: state.currentPage,
      //   to: state.currentPage - 1,
      //   hasPrevQuery: !!state.prevQuery,
      // });
      return {
        ...state,
        isLoading: true,
        currentPage: state.currentPage - 1,
        query: state.prevQuery,
      };
    }
    case "GO-TO": {
      console.log("goto --> ", action.payload);
      const page = action.payload;
      const offset = (page - 1) * state.limit;

      const getStartAfterDoc = async () => {
        const allDocs = await getDocs(state.query);
        const startAfterDoc = allDocs.docs[offset];
        return startAfterDoc;
      };
      return {
        ...state,
        // goto: action.payload,
        isLoading: true,
        currentPage: page,
        startAfterDoc: getStartAfterDoc(), // Store the startAfterDoc promise
      };
    }

    default: {
      return defaultGuard(state, action);
    }
  }
};

const initialState = {
  query: undefined,
  queryRef: undefined,
  lastQuery: undefined,
  firstDocRef: undefined,
  docs: [],
  firstDoc: undefined,
  lastDoc: undefined,
  prevQuery: undefined,
  nextQuery: undefined,
  items: [],
  isLoading: true,
  isStart: true,
  isEnd: false,
  totalItems: 0,
  totalPages: 0,
  currentPage: 0,
  limit: 10,
  startAfterDoc: null,
  pageCache: {}, // Cache first document of each visited page
};

const usePagination = (firestoreQuery, options) => {
  const [state, dispatch] = useReducer(getReducer(), initialState);
  const queryRef = useRef(undefined);
  const firstDocRef = useRef(undefined);

  const { limit: limitOpt = 10 } = options;

  useEffect(async () => {
    if (firestoreQuery !== undefined) {
      if (
        queryRef.current &&
        queryEqual(firestoreQuery, queryRef.current) &&
        limitOpt === state.limit
      ) {
        return;
      }
      queryRef.current = firestoreQuery;

      let totalItems = await getCountFromServer(firestoreQuery);

      firstDocRef.current = undefined;

      dispatch({
        type: "SET-QUERY",
        payload: {
          query: firestoreQuery,
          queryRef,
          firstDocRef,
          limit: limitOpt,
          totalItems: totalItems.data().count,
          totalPages: Math.ceil(totalItems.data().count / limitOpt),
          currentPage: 1,
        },
      });
    }
  }, [firestoreQuery, limitOpt, state.limit]);

  useEffect(() => {
    if (state.query !== undefined) {
      // let q = state.query;
      let q = query(state.query, limit(state.limit));
      // const unsubscribe = onSnapshot(state.query, (snap) => {
      //   if (state.query) {
      //     dispatch({
      //       type: "LOAD",
      //       payload: { value: snap, query: state.query },
      //     });
      //   }
      // });

      // return () => unsubscribe();

      if (state.startAfterDoc instanceof Promise) {
        state.startAfterDoc.then((startAfterDoc) => {
          if (startAfterDoc) {
            q = query(
              state.query,
              startAfter(startAfterDoc),
              limit(state.limit)
            );
          }
          const unsubscribe = onSnapshot(q, (snap) => {
            if (q) {
              dispatch({
                type: "LOAD",
                payload: { value: snap, query: q },
              });
            }
          });

          return () => unsubscribe();
        });
      } else {
        const unsubscribe = onSnapshot(q, (snap) => {
          if (q) {
            dispatch({
              type: "LOAD",
              payload: { value: snap, query: q },
            });
          }
        });
        return () => unsubscribe();
      }
    }
  }, [state.query, state.startAfterDoc, state.limit, state.queryRef]);

  const goToPage = (page) => {
    if (page >= 1 && page <= state.totalPages) {
      dispatch({ type: "GO-TO", payload: page });
    }
  };

  return {
    docs: state.docs,
    items: state.items,
    isLoading: state.isLoading,
    isStart: state.isStart,
    isEnd: state.isEnd,
    totalItems: state.totalItems,
    totalPages: state.totalPages,
    currentPage: state.currentPage,
    goToPage: goToPage,
    // setPage: (p) => dispatch({ type: "GO-TO", payload: p }),
    getPrev: () => dispatch({ type: "PREV" }),
    getNext: () => dispatch({ type: "NEXT" }),
  };
};

export { usePagination };
